import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { StyleSheet, View, TextInput, Pressable, Keyboard, Platform } from "react-native";
import { router } from "expo-router";
import Animated from "react-native-reanimated";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { Button } from "@/components/ui/Button";
import { twilioSmsService, SMSConversation } from "@/services/twilioSmsService";
import { useSMSPhone } from "./_layout";
import { useSMS } from "@/hooks/useSMS";
import { supabase } from "@/lib/supabase-client";
import { formatPhoneNumber, normalizePhoneNumber, isValidPhoneNumber } from "@/lib/phone";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Head from "expo-router/head";
import Avatar from "@/components/Avatar";
import useAuth from "@/hooks/useAuth";
import { userService } from "@/services/userService";

interface SearchResult {
  id: string;
  name: string;
  phone: string;
  type: "user" | "contact" | "recent";
}

type MatchedUser = {
  id: string;
  name: string;
};

// Normalize phone to 10-digit format (strip +1 or leading 1)
function normalizeToTenDigits(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits;
}

export default function NewSMSPage() {
  const { phoneNumber: twilioNumber } = useSMSPhone();
  const { phones } = useSMS();
  const { colors } = useTheme();
  const { user } = useAuth();
  const [userSignature, setUserSignature] = useState("");

  // Fetch current user's profile for signature
  useEffect(() => {
    if (!user?.id) return;

    userService.getUserById(user.id).then((profile) => {
      if (profile) {
        setUserSignature(profile.preferred_name || profile.first_name || "");
      }
    });
  }, [user?.id]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState<SearchResult | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search results
  const [users, setUsers] = useState<SearchResult[]>([]);
  const [recentConversations, setRecentConversations] = useState<SMSConversation[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [usersByPhone, setUsersByPhone] = useState<Map<string, MatchedUser>>(new Map());

  const inputRef = useRef<TextInput>(null);
  const messageInputRef = useRef<TextInput>(null);

  // Get display name for the current phone
  const currentPhoneDisplay = useMemo(() => {
    const phone = phones.find((p) => p.number === twilioNumber);
    return phone?.displayName || formatPhoneNumber(twilioNumber);
  }, [phones, twilioNumber]);

  // Load recent conversations
  useEffect(() => {
    if (!twilioNumber) return;

    (async () => {
      try {
        const conversations = await twilioSmsService.getConversations(twilioNumber);
        setRecentConversations(conversations.slice(0, 5)); // Show last 5
      } catch (err) {
        console.error("Failed to load recent conversations:", err);
      }
    })();
  }, [twilioNumber]);

  // Load users for phone lookup (for recent conversations)
  useEffect(() => {
    if (recentConversations.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const candidateNumbers = recentConversations.map((c) => c.externalNumber);
        const users = await userService.getUsersByCellPhones(candidateNumbers);
        if (cancelled) return;
        const map = new Map<string, MatchedUser>();
        for (const u of users) {
          map.set(u.last10, {
            id: u.id,
            name: u.preferred_name || `${u.first_name || ""} ${u.last_name || ""}`.trim(),
          });
        }
        setUsersByPhone(map);
      } catch (err) {
        console.error("Failed to load users for phone lookup:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recentConversations]);

  // Helper to get matched user for a phone number
  const getMatchedUser = useCallback(
    (phone: string): MatchedUser | null => {
      const normalized = normalizeToTenDigits(phone);
      return usersByPhone.get(normalized) || null;
    },
    [usersByPhone],
  );

  // Search users by name (get cell phone from meta_data.phones)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setUsers([]);
      return;
    }

    const search = async () => {
      setLoadingSearch(true);
      try {
        // Search users via users table with meta_data.phones
        const { data: usersData } = await supabase
          .from("users")
          .select("id, first_name, last_name, preferred_name, meta_data");

        const query = searchQuery.toLowerCase();

        // Filter and map users (get cell phone from meta_data.phones)
        const matchingUsers: SearchResult[] = [];
        if (usersData) {
          for (const user of usersData) {
            const fullName = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
            const preferred = (user.preferred_name || "").toLowerCase();

            if (!fullName.includes(query) && !preferred.includes(query)) continue;

            // Get cell phone from meta_data.phones
            const phones = (user.meta_data as any)?.phones || [];
            const cellPhone = phones.find((p: any) => p.type === "cell");

            if (cellPhone?.number) {
              matchingUsers.push({
                id: user.id,
                name: user.preferred_name || `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                phone: cellPhone.number,
                type: "user",
              });
            }
          }
        }

        setUsers(matchingUsers);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setLoadingSearch(false);
      }
    };

    const debounce = setTimeout(search, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Check if the search query itself is a phone number
  const isPhoneInput = useMemo(() => {
    const cleaned = searchQuery.replace(/[^0-9+]/g, "");
    return cleaned.length >= 7;
  }, [searchQuery]);

  const handleSelectResult = useCallback((result: SearchResult) => {
    setSelectedRecipient(result);
    setSearchQuery("");
    messageInputRef.current?.focus();
  }, []);

  const handleSelectRecent = useCallback(
    (conv: SMSConversation) => {
      const matchedUser = getMatchedUser(conv.externalNumber);
      setSelectedRecipient({
        id: matchedUser?.id || conv.externalNumber,
        name: matchedUser?.name || formatPhoneNumber(conv.externalNumber),
        phone: conv.externalNumber,
        type: matchedUser ? "user" : "recent",
      });
      setSearchQuery("");
      messageInputRef.current?.focus();
    },
    [getMatchedUser],
  );

  const handleSelectPhoneInput = useCallback(() => {
    const normalized = normalizePhoneNumber(searchQuery);
    if (isValidPhoneNumber(normalized)) {
      setSelectedRecipient({
        id: normalized,
        name: formatPhoneNumber(normalized),
        phone: normalized,
        type: "recent",
      });
      setSearchQuery("");
      messageInputRef.current?.focus();
    }
  }, [searchQuery]);

  const handleClearRecipient = useCallback(() => {
    setSelectedRecipient(null);
    inputRef.current?.focus();
  }, []);

  const sendMessage = async () => {
    if (!selectedRecipient || !draft.trim() || sending) return;

    // Append signature to message
    const messageWithSignature = userSignature ? `${draft.trim()}\n- ${userSignature}` : draft.trim();

    try {
      setSending(true);
      setError(null);
      // Canonicalize to E.164 so the conversation key matches what we persist (Twilio's
      // E.164) — keeps the route, DB lookups, realtime filter, and read markers aligned.
      const externalNumber = normalizePhoneNumber(selectedRecipient.phone);
      await twilioSmsService.sendMessage(twilioNumber, externalNumber, messageWithSignature);

      // Navigate to the conversation
      const encodedPhone = encodeURIComponent(twilioNumber);
      const encodedExternal = encodeURIComponent(externalNumber);
      router.replace(`/chat/sms/${encodedPhone}/${encodedExternal}`);
    } catch (err: any) {
      console.error("Failed to send SMS:", err);
      setError(err.message || "Failed to send message");
      setSending(false);
    }
  };

  const hasResults = users.length > 0 || isPhoneInput;

  return (
    <GestureHandlerRootView style={styles.container}>
      <Head>
        <title>New SMS - RockForce</title>
        <meta name="description" content="Start a new SMS conversation" />
      </Head>

      {/* Header */}
      <View style={[styles.header, { borderColor: colors.border }]}>
        <ThemedText style={styles.headerTitle}>New SMS from {currentPhoneDisplay}</ThemedText>
      </View>

      {/* Recipient Input */}
      <View style={[styles.recipientSection, { borderColor: colors.border }]}>
        <ThemedText style={[styles.toLabel, { color: colors.placeholder }]}>To:</ThemedText>
        {selectedRecipient ? (
          <View style={[styles.selectedRecipient, { backgroundColor: colors.primary + "20" }]}>
            <ThemedText style={[styles.selectedRecipientText, { color: colors.primary }]}>
              {selectedRecipient.name}
              {selectedRecipient.type !== "recent" && ` (${formatPhoneNumber(selectedRecipient.phone)})`}
            </ThemedText>
            <Pressable onPress={handleClearRecipient}>
              <MaterialCommunityIcons name="close-circle" size={18} color={colors.primary} />
            </Pressable>
          </View>
        ) : (
          <TextInput
            ref={inputRef}
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search people or enter phone number..."
            placeholderTextColor={colors.placeholder}
            style={[styles.recipientInput, { color: colors.text }]}
            autoFocus
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="default"
          />
        )}
      </View>

      {/* Search Results */}
      {!selectedRecipient && (searchQuery || recentConversations.length > 0) && (
        <Animated.ScrollView
          style={[styles.resultsContainer, { borderColor: colors.border }]}
          contentContainerStyle={styles.resultsContent}
          keyboardShouldPersistTaps="handled"
        >
          {/* Manual Phone Number Entry */}
          {isPhoneInput && (
            <View style={styles.section}>
              <Pressable
                style={({ hovered }) => [styles.resultItem, hovered && { backgroundColor: colors.border + "40" }]}
                onPress={handleSelectPhoneInput}
              >
                <MaterialCommunityIcons name="phone-plus" size={20} color={colors.primary} />
                <View style={styles.resultInfo}>
                  <ThemedText style={styles.resultName}>
                    Send to {formatPhoneNumber(normalizePhoneNumber(searchQuery))}
                  </ThemedText>
                </View>
              </Pressable>
            </View>
          )}

          {/* Users */}
          {users.length > 0 && (
            <View style={styles.section}>
              <ThemedText style={[styles.sectionTitle, { color: colors.placeholder }]}>Users</ThemedText>
              {users.map((user) => (
                <Pressable
                  key={user.id}
                  style={({ hovered }) => [styles.resultItem, hovered && { backgroundColor: colors.border + "40" }]}
                  onPress={() => handleSelectResult(user)}
                >
                  <Avatar userId={user.id} size={32} />
                  <View style={styles.resultInfo}>
                    <ThemedText style={styles.resultName}>{user.name}</ThemedText>
                    <ThemedText style={[styles.resultPhone, { color: colors.placeholder }]}>
                      {formatPhoneNumber(user.phone)}
                    </ThemedText>
                  </View>
                </Pressable>
              ))}
            </View>
          )}

          {/* Recent Conversations */}
          {!searchQuery && recentConversations.length > 0 && (
            <View style={styles.section}>
              <ThemedText style={[styles.sectionTitle, { color: colors.placeholder }]}>Recent</ThemedText>
              {recentConversations.map((conv) => {
                const matchedUser = getMatchedUser(conv.externalNumber);
                return (
                  <Pressable
                    key={conv.externalNumber}
                    style={({ hovered }) => [styles.resultItem, hovered && { backgroundColor: colors.border + "40" }]}
                    onPress={() => handleSelectRecent(conv)}
                  >
                    {matchedUser ? (
                      <Avatar userId={matchedUser.id} size={32} />
                    ) : (
                      <MaterialCommunityIcons name="history" size={20} color={colors.textSecondary} />
                    )}
                    <View style={styles.resultInfo}>
                      <ThemedText style={styles.resultName}>
                        {matchedUser?.name || formatPhoneNumber(conv.externalNumber)}
                      </ThemedText>
                      {matchedUser && (
                        <ThemedText style={[styles.resultPhone, { color: colors.placeholder }]}>
                          {formatPhoneNumber(conv.externalNumber)}
                        </ThemedText>
                      )}
                      <ThemedText style={[styles.resultPreview, { color: colors.placeholder }]} numberOfLines={1}>
                        {conv.lastMessage}
                      </ThemedText>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}

          {/* No Results */}
          {searchQuery && !hasResults && !loadingSearch && (
            <View style={styles.noResults}>
              <ThemedText style={[styles.noResultsText, { color: colors.placeholder }]}>
                No matching people found
              </ThemedText>
            </View>
          )}
        </Animated.ScrollView>
      )}

      {/* Empty State */}
      {!selectedRecipient && !searchQuery && recentConversations.length === 0 && (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="message-text-outline" size={64} color={colors.textSecondary} />
          <ThemedText style={styles.emptyTitle}>Start a New SMS</ThemedText>
          <ThemedText style={[styles.emptyDescription, { color: colors.placeholder }]}>
            Search for a person or enter a phone number above
          </ThemedText>
        </View>
      )}

      {/* Message Input */}
      {selectedRecipient && (
        <View style={styles.messageSection}>
          {error && (
            <View style={[styles.errorBanner, { backgroundColor: colors.dangerBackground }]}>
              <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>{error}</ThemedText>
            </View>
          )}

          <View style={[styles.inputContainer, { borderColor: colors.border, backgroundColor: colors.card }]}>
            <View style={[styles.inputWrapper, { borderColor: colors.border, backgroundColor: colors.background }]}>
              <TextInput
                ref={messageInputRef}
                value={draft}
                onChangeText={setDraft}
                placeholder="Write a message..."
                placeholderTextColor={colors.placeholder}
                style={[styles.messageInput, { color: colors.text }]}
                autoCorrect
                autoCapitalize="sentences"
                blurOnSubmit={false}
                editable={!sending}
                multiline={Platform.OS === "web"}
                onSubmitEditing={() => {
                  if (Platform.OS !== "web") sendMessage();
                }}
                onKeyPress={(e) => {
                  if (Platform.OS === "web") {
                    const k = e.nativeEvent.key;
                    if (k === "Enter" && !(e.nativeEvent as any).shiftKey) {
                      (e as any).preventDefault?.();
                      sendMessage();
                    }
                  }
                }}
              />
            </View>
            <Button
              style={styles.sendButton}
              icon={sending ? "loading" : "send"}
              onPress={() => {
                sendMessage();
                Keyboard.dismiss();
              }}
              enabled={draft.trim().length > 0 && !sending}
            />
          </View>
        </View>
      )}
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  recipientSection: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 8,
  },
  toLabel: {
    fontSize: 15,
    fontWeight: "500",
  },
  recipientInput: {
    flex: 1,
    fontSize: 15,
    paddingVertical: 4,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  selectedRecipient: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
  },
  selectedRecipientText: {
    fontSize: 14,
    fontWeight: "500",
  },
  resultsContainer: {
    flex: 1,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  resultsContent: {
    paddingVertical: 8,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  resultItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 12,
  },
  resultInfo: {
    flex: 1,
    gap: 2,
  },
  resultName: {
    fontSize: 15,
    fontWeight: "500",
  },
  resultPhone: {
    fontSize: 13,
  },
  resultPreview: {
    fontSize: 13,
  },
  noResults: {
    padding: 32,
    alignItems: "center",
  },
  noResultsText: {
    fontSize: 14,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: "600",
    marginTop: 16,
  },
  emptyDescription: {
    fontSize: 14,
    textAlign: "center",
  },
  messageSection: {
    marginTop: "auto",
  },
  errorBanner: {
    padding: 12,
    marginHorizontal: 12,
    marginBottom: 8,
    borderRadius: 8,
  },
  errorText: {
    fontSize: 13,
    textAlign: "center",
  },
  inputContainer: {
    flexDirection: "row",
    borderTopWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
    gap: 12,
    padding: 12,
  },
  inputWrapper: {
    flex: 1,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: Platform.OS === "web" ? 6 : 0,
    justifyContent: "center",
  },
  messageInput: {
    minHeight: 36,
    fontSize: 16,
    paddingVertical: 6,
    paddingHorizontal: 4,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  sendButton: {
    height: 36,
    width: 36,
    borderRadius: 999,
    transform: [{ rotate: "-90deg" }],
  },
});
